import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getToken } from '../services/api';

const STEPS = {
  INPUT: 1,
  PROGRESS: 2,
  RESULT: 3,
};

const AIStoryboard = () => {
  const { t } = useTranslation();

  // Step state
  const [step, setStep] = useState(STEPS.INPUT);

  // Input state
  const [text, setText] = useState('');
  const [voice, setVoice] = useState('SunHi');
  const [scenes, setScenes] = useState('auto');
  const [style, setStyle] = useState('dark');

  // Progress state
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const eventSourceRef = useRef(null);

  // Result state
  const [videoUrl, setVideoUrl] = useState('');
  const [duration, setDuration] = useState(0);
  const [sceneCount, setSceneCount] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  // Error state
  const [error, setError] = useState(null);

  // History state
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Timer for elapsed
  const startTimeRef = useRef(null);
  const timerRef = useRef(null);

  const cleanupSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      const token = getToken();
      if (!token) return;
      const res = await fetch('/api/ai/history?type=storyboard&limit=10', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setHistory(data.jobs || []);
      }
    } catch (err) {
      console.error('History fetch error:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    return () => cleanupSSE();
  }, [cleanupSSE]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const fetchResult = useCallback(async (id) => {
    try {
      const token = getToken();
      const res = await fetch(`/api/ai/storyboard/result/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch result');
      const data = await res.json();

      setVideoUrl(data.result?.videoUrl || "");
      setDuration(data.result?.duration || 0);
      setSceneCount(data.result?.scenes || 0);

      if (startTimeRef.current) {
        setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
      }

      cleanupSSE();
      setStep(STEPS.RESULT);
      fetchHistory();
    } catch (err) {
      setError(t('storyboard.error.failed'));
      cleanupSSE();
      setStep(STEPS.INPUT);
    }
  }, [cleanupSSE, t, fetchHistory]);

  const connectSSE = useCallback((id) => {
    const token = getToken();
    const url = `/api/ai/storyboard/progress/${id}?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.progress !== undefined) setProgress(data.progress);
        if (data.step !== undefined) setCurrentStep(data.step);
        if (data.stepName) setProgressMessage(data.stepName);

        if (data.status === 'completed' || data.progress >= 100) {
          fetchResult(id);
        } else if (data.status === 'failed') {
          setError(data.error || t('storyboard.error.failed'));
          cleanupSSE();
          setStep(STEPS.INPUT);
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      es.close();
      fetchResult(id);
    };
  }, [fetchResult, t, cleanupSSE]);

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError(t('storyboard.error.empty'));
      return;
    }

    setError(null);
    setStep(STEPS.PROGRESS);
    setProgress(0);
    setCurrentStep(0);
    setProgressMessage('');
    startTimeRef.current = Date.now();

    // Start elapsed timer
    timerRef.current = setInterval(() => {
      if (startTimeRef.current) {
        setElapsed(Math.round((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    try {
      const token = getToken();
      const res = await fetch('/api/ai/storyboard/generate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text.trim(),
          voice,
          scenes: scenes === 'auto' ? null : parseInt(scenes),
          style,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Generation failed');
      }

      const data = await res.json();
      const newJobId = data.jobId;
      setJobId(newJobId);

      // Connect SSE for progress
      connectSSE(newJobId);
    } catch (err) {
      setError(err.message || t('storyboard.error.failed'));
      cleanupSSE();
      setStep(STEPS.INPUT);
    }
  };

  const handleDownload = () => {
    if (!jobId) return;
    const token = getToken();
    window.open(`/api/ai/storyboard/download/${jobId}?token=${encodeURIComponent(token)}`, '_blank');
  };

  const handleReset = () => {
    cleanupSSE();
    setStep(STEPS.INPUT);
    setText('');
    setVoice('SunHi');
    setScenes('auto');
    setStyle('dark');
    setJobId(null);
    setProgress(0);
    setCurrentStep(0);
    setProgressMessage('');
    setVideoUrl('');
    setDuration(0);
    setSceneCount(0);
    setElapsed(0);
    setError(null);
    startTimeRef.current = null;
  };

  const formatDuration = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const progressSteps = [
    t('storyboard.progress.step1'),
    t('storyboard.progress.step2'),
    t('storyboard.progress.step3'),
    t('storyboard.progress.step4'),
    t('storyboard.progress.step5'),
  ];

  return (
    <div className="p-6 px-8 font-sans max-w-4xl mx-auto">
      {/* Header */}
      <header className="mb-8 pb-5 border-b border-gray-200">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">{t('storyboard.title')}</h1>
        <p className="text-sm text-gray-600">{t('storyboard.subtitle')}</p>
      </header>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <span className="text-red-500 text-lg">⚠️</span>
          <span className="text-red-700 text-sm flex-1">{error}</span>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-600 text-lg leading-none"
          >
            ×
          </button>
        </div>
      )}

      {/* Step 1: Input */}
      {step === STEPS.INPUT && (
        <div className="space-y-6">
          {/* Textarea */}
          <div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('storyboard.input.placeholder')}
              rows={8}
              className="w-full p-4 rounded-xl border border-gray-300 bg-white text-base resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
            />
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Voice */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('storyboard.input.voice')}
              </label>
              <select
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                className="w-full p-2.5 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="SunHi">{t('storyboard.input.voiceSunhi')}</option>
                <option value="InJoon">{t('storyboard.input.voiceInjoon')}</option>
              </select>
            </div>

            {/* Scenes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('storyboard.input.scenes')}
              </label>
              <select
                value={scenes}
                onChange={(e) => setScenes(e.target.value)}
                className="w-full p-2.5 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="auto">{t('storyboard.input.scenesAuto')}</option>
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="15">15</option>
                <option value="20">20</option>
              </select>
            </div>

            {/* Style */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {t('storyboard.input.style')}
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="w-full p-2.5 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="dark">{t('storyboard.input.styleDark')}</option>
                <option value="light">{t('storyboard.input.styleLight')}</option>
                <option value="accent">{t('storyboard.input.styleAccent')}</option>
              </select>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={!text.trim()}
            className="w-full py-3.5 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl text-base transition-colors duration-200"
          >
            {t('storyboard.input.generate')}
          </button>
        </div>
      )}

      {/* Step 2: Progress */}
      {step === STEPS.PROGRESS && (
        <div className="space-y-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('storyboard.progress.title')}
            </h2>
            {progressMessage && (
              <p className="text-sm text-gray-500">{progressMessage}</p>
            )}
          </div>

          {/* Progress Bar */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">
                {formatDuration(elapsed)}
              </span>
              <span className="text-sm font-medium text-blue-600">
                {Math.min(progress, 100)}%
              </span>
            </div>
            <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>

          {/* Step Checklist */}
          <div className="space-y-3">
            {progressSteps.map((label, idx) => {
              const stepNum = idx + 1;
              const isDone = currentStep > stepNum;
              const isCurrent = currentStep === stepNum;
              const isPending = currentStep < stepNum;

              return (
                <div
                  key={idx}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isCurrent ? 'bg-blue-50 border border-blue-200' :
                    isDone ? 'bg-green-50 border border-green-200' :
                    'bg-gray-50 border border-gray-100'
                  }`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
                    {isDone && (
                      <span className="text-green-600 text-lg">✓</span>
                    )}
                    {isCurrent && (
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    )}
                    {isPending && (
                      <span className="text-gray-300 text-lg">○</span>
                    )}
                  </div>

                  {/* Label */}
                  <span className={`text-sm ${
                    isDone ? 'text-green-700 font-medium' :
                    isCurrent ? 'text-blue-700 font-medium' :
                    'text-gray-400'
                  }`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 3: Result */}
      {step === STEPS.RESULT && (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('storyboard.result.title')}
            </h2>
          </div>

          {/* Video Player */}
          <div className="rounded-xl overflow-hidden bg-black">
            <video
              src={videoUrl}
              controls
              className="w-full max-h-[480px]"
              autoPlay={false}
            />
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-xs text-gray-500 mb-1">{t('storyboard.result.duration')}</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatDuration(Math.round(duration))}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-xs text-gray-500 mb-1">{t('storyboard.result.sceneCount')}</div>
              <div className="text-lg font-semibold text-gray-900">{sceneCount}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-xs text-gray-500 mb-1">{t('storyboard.result.elapsed')}</div>
              <div className="text-lg font-semibold text-gray-900">
                {formatDuration(elapsed)}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleDownload}
              className="flex-1 py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl text-base transition-colors duration-200"
            >
              {t('storyboard.result.download')}
            </button>
            <button
              onClick={handleReset}
              className="flex-1 py-3 px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl text-base transition-colors duration-200"
            >
              {t('storyboard.result.newVideo')}
            </button>
          </div>
        </div>
      )}

      {/* 작업 히스토리 */}
      <section className="mt-10 pt-8 border-t border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">📋 작업 히스토리</h2>
        {isLoadingHistory ? (
          <div className="text-center py-8 text-gray-400 text-sm">불러오는 중...</div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">아직 작업 기록이 없습니다.</div>
        ) : (
          <div className="space-y-3">
            {history.map((item) => (
              <div key={item.jobId} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                    item.status === 'completed' ? 'bg-green-100 text-green-700' :
                    item.status === 'failed' ? 'bg-red-100 text-red-700' :
                    item.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {item.status === 'completed' ? '완료' : item.status === 'failed' ? '실패' : item.status === 'processing' ? '처리중' : '대기'}
                  </span>
                  <span className="text-sm text-gray-700 truncate">
                    {item.parameters?.style ? `${item.parameters.style} 스타일` : '스토리보드'}
                    {item.parameters?.scenes ? ` · ${item.parameters.scenes}장면` : ''}
                  </span>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0 ml-4">
                  {new Date(item.createdAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default AIStoryboard;
